import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import { ArrowRight, CheckCircle, WarningCircle, EnvelopeSimple, MapPin, Phone } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: '', company: '', email: '', phone: '', message: '' };

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrMsg('');
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 15000 });
      if (res?.data?.status === 'success') {
        setStatus('success');
        setForm(initial);
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrMsg(typeof detail === 'string' ? detail : t.contact.error);
      setStatus('error');
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contact.root}
      className="relative bg-[#0A1128] text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.contactBuilding} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128] via-[#0A1128]/85 to-[#0A1128]" />
      <div className="absolute inset-0 bg-grid-lines opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading + meta */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[#4F6355]" />
              <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">
                {t.contact.overline}
              </span>
            </motion.div>

            <h2 className="font-display font-bold tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-white">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-md">
              {t.contact.body}
            </p>

            <div className="mt-10 flex flex-col gap-4">
              <ContactMeta Icon={EnvelopeSimple} label="Email" value="rd@cdpa.ro" />
              <ContactMeta Icon={Phone} label="Phone" value="+40 757 028 028" />
              <ContactMeta Icon={MapPin} label="HQ" value="București, Romania" />
            </div>

            <div className="mt-12 border-t border-white/10 pt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">
              Secure inquiry channel · Encrypted in transit
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              data-testid={TID.contact.form}
              onSubmit={onSubmit}
              className="bg-[#151F32]/80 backdrop-blur-md border border-white/10 p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  testId={TID.contact.name}
                  label={t.contact.labels.name}
                  name="name"
                  required
                  placeholder={t.contact.placeholders.name}
                  value={form.name}
                  onChange={onChange}
                />
                <Field
                  testId={TID.contact.company}
                  label={t.contact.labels.company}
                  name="company"
                  placeholder={t.contact.placeholders.company}
                  value={form.company}
                  onChange={onChange}
                />
                <Field
                  testId={TID.contact.email}
                  label={t.contact.labels.email}
                  name="email"
                  type="email"
                  required
                  placeholder={t.contact.placeholders.email}
                  value={form.email}
                  onChange={onChange}
                />
                <Field
                  testId={TID.contact.phone}
                  label={t.contact.labels.phone}
                  name="phone"
                  placeholder={t.contact.placeholders.phone}
                  value={form.phone}
                  onChange={onChange}
                />
              </div>

              <div className="mt-5">
                <label className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#94A3B8]">
                  {t.contact.labels.message}
                </label>
                <textarea
                  data-testid={TID.contact.message}
                  name="message"
                  required
                  rows={6}
                  placeholder={t.contact.placeholders.message}
                  value={form.message}
                  onChange={onChange}
                  className="mt-2 w-full bg-[#0A1128] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#4F6355] focus:ring-1 focus:ring-[#4F6355] transition-colors resize-none"
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <button
                  data-testid={TID.contact.submit}
                  type="submit"
                  disabled={status === 'loading'}
                  className="btn-primary inline-flex items-center justify-center gap-3 px-7 py-4 text-[11px] tracking-[0.22em] uppercase font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? t.cta.sending : t.cta.sendInquiry}
                  <ArrowRight size={14} weight="bold" />
                </button>

                {status === 'success' && (
                  <div
                    data-testid={TID.contact.success}
                    className="flex items-center gap-2 text-sm text-[#4F6355]"
                  >
                    <CheckCircle weight="duotone" size={18} />
                    {t.contact.success}
                  </div>
                )}

                {status === 'error' && (
                  <div
                    data-testid={TID.contact.error}
                    className="flex items-center gap-2 text-sm text-red-300"
                  >
                    <WarningCircle weight="duotone" size={18} />
                    {errMsg || t.contact.error}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ testId, label, name, type = 'text', required, placeholder, value, onChange }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#94A3B8]">
        {label}
        {required && <span className="text-[#4F6355] ml-1">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="mt-2 w-full bg-[#0A1128] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#4F6355] focus:ring-1 focus:ring-[#4F6355] transition-colors"
      />
    </div>
  );
}

function ContactMeta({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-white/10">
      <div className="w-10 h-10 flex items-center justify-center border border-white/10 bg-white/5 text-[#4F6355]">
        <Icon size={18} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] font-semibold">{label}</div>
        <div className="text-sm md:text-base text-white font-medium tracking-tight mt-1">{value}</div>
      </div>
    </div>
  );
}
